import React, { useState } from "react";
import { MagicWand, Copy, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ModelSelector } from "../components/ModelSelector";

const NICHES = ["gaming", "finance", "motivation", "ai", "fitness", "tech", "storytelling"];
const TONES = ["energetic", "educational", "dramatic", "funny", "inspiring"];

export default function ScriptStudio() {
  const { setCredits } = useAuth();
  const [niche, setNiche] = useState("ai");
  const [topic, setTopic] = useState("");
  const [duration, setDuration] = useState(30);
  const [tone, setTone] = useState("energetic");
  const [model, setModel] = useState(null);
  const [compare, setCompare] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!topic.trim()) { toast.error("Add a topic"); return; }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/ai/script", { niche, topic, duration_seconds: duration, tone, model, compare });
      if (typeof data?._credits_remaining === "number") setCredits(data._credits_remaining);
      setResult(data);
      toast.success(data.compare ? "Compared across models" : "Script generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="script-studio">
      <p className="text-[#FF3B6B] text-sm font-mono">// AI SCRIPT STUDIO</p>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Forge a viral script</h1>
      <p className="text-white/50 mt-1">Hook · Body · CTA · Viral score · Hashtags</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 glass rounded-xl p-6 space-y-5">
          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Niche</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {NICHES.map((n) => (
                <button key={n} onClick={() => setNiche(n)} className={`px-3 py-1.5 rounded-full text-xs capitalize transition-all ${niche === n ? "bg-[#FF3B6B] text-white" : "bg-white/5 text-white/70 border border-white/10 hover:bg-white/10"}`} data-testid={`niche-btn-${n}`}>
                  {n}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-xs text-white/60 uppercase tracking-wider">Topic</label>
            <textarea value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. 3 AI tools every creator should use in 2026" rows={3} className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm focus:border-[#FF3B6B] focus:outline-none resize-none" data-testid="script-topic-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-white/60 uppercase tracking-wider">Duration</label>
              <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                <SelectTrigger className="mt-2 bg-white/5 border-white/10" data-testid="script-duration-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[15, 30, 45, 60].map(d => <SelectItem key={d} value={String(d)}>{d}s</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-white/60 uppercase tracking-wider">Tone</label>
              <Select value={tone} onValueChange={setTone}>
                <SelectTrigger className="mt-2 bg-white/5 border-white/10 capitalize" data-testid="script-tone-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TONES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <ModelSelector
            type="text"
            action="script"
            model={model}
            compare={compare}
            onChange={({ model: m, compare: c }) => { setModel(m); setCompare(c); }}
          />

          <button onClick={submit} disabled={loading} className="w-full btn-primary py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="generate-script-btn">
            {loading ? <><span className="shimmer w-3 h-3 rounded-full bg-white/40" />{compare ? "Comparing 3 models..." : "Generating..."}</> : <><Sparkle weight="fill" size={16} /> {compare ? "Compare across models" : "Generate script"}</>}
          </button>
        </div>

        <div className="lg:col-span-7 space-y-4">
          {!result && !loading && (
            <div className="glass rounded-xl p-12 text-center text-white/40 border-dashed">
              <MagicWand size={40} className="mx-auto mb-3 text-white/20" />
              <p>Your script will appear here</p>
            </div>
          )}
          {loading && (
            <div className="glass rounded-xl p-8 space-y-3">
              {[1, 2, 3].map(i => <div key={i} className="shimmer h-4 rounded bg-white/5" />)}
              <div className="shimmer h-20 rounded bg-white/5" />
            </div>
          )}
          {result && !result.compare && (
            <ScriptCards data={result} copy={copy} />
          )}
          {result?.compare && (
            <div className="space-y-6">
              {result.variants.map((v) => (
                <div key={v.model} className="space-y-3" data-testid={`variant-${v.model}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono px-2 py-1 rounded-full bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/30">
                      {v.label}
                    </span>
                    {v.error && <span className="text-xs text-red-400">failed: {v.error}</span>}
                  </div>
                  {v.data && <ScriptCards data={v.data} copy={copy} />}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const ScriptCards = ({ data, copy }) => (
  <>
    <div className="glass rounded-xl p-6" data-testid="script-result-hook">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-[#FF3B6B] font-mono uppercase tracking-wider">Hook</p>
        <div className="flex items-center gap-3">
          <div className="font-mono text-xs">
            <span className="text-white/50">Viral score: </span>
            <span className="text-[#FF3B6B] text-lg font-bold">{data.viral_score}</span>
            <span className="text-white/50">/100</span>
          </div>
          <button onClick={() => copy(data.hook, "Hook")} className="text-white/40 hover:text-white" data-testid="copy-hook"><Copy size={14} /></button>
        </div>
      </div>
      <p className="text-lg font-medium">{data.hook}</p>
    </div>

    <div className="glass rounded-xl p-6" data-testid="script-result-body">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-[#00E5FF] font-mono uppercase tracking-wider">Body</p>
        <button onClick={() => copy(data.body, "Body")} className="text-white/40 hover:text-white"><Copy size={14} /></button>
      </div>
      <p className="text-white/80 whitespace-pre-wrap leading-relaxed">{data.body}</p>
    </div>

    <div className="glass rounded-xl p-6" data-testid="script-result-cta">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-[#EAB308] font-mono uppercase tracking-wider">CTA</p>
        <button onClick={() => copy(data.cta, "CTA")} className="text-white/40 hover:text-white"><Copy size={14} /></button>
      </div>
      <p className="font-medium">{data.cta}</p>
    </div>

    <div className="glass rounded-xl p-6" data-testid="script-result-hashtags">
      <p className="text-xs text-white/50 font-mono uppercase tracking-wider mb-2">Hashtags</p>
      <div className="flex flex-wrap gap-2">
        {data.hashtags?.map((h, i) => (
          <span key={i} className="text-sm text-[#00E5FF] font-mono">#{h.replace(/^#/, "")}</span>
        ))}
      </div>
    </div>
  </>
);
